import { AsyncPipe, CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { NxErrorModule } from "@aposin/ng-aquila/base";
import { NxButtonModule } from "@aposin/ng-aquila/button";
import { NxFormfieldModule } from "@aposin/ng-aquila/formfield";
import { NxGridModule } from "@aposin/ng-aquila/grid";
import { NxIconModule } from "@aposin/ng-aquila/icon";
import { NxInputModule } from "@aposin/ng-aquila/input";
import { NxLinkModule } from "@aposin/ng-aquila/link";
import { NxMessageModule } from "@aposin/ng-aquila/message";
import { NxPopoverModule } from "@aposin/ng-aquila/popover";
import { CommonModalComponent } from "@modules/modals/common-modal/common-modal.component";
import { ItHelpdeskModalComponent } from "@modules/modals/it-helpdesk-modal/it-helpdesk-modal.component";
import { TranslateModule } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { CredentialStorageService } from "@services/credential-storage/credential-storage.service";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import { concurrentLoginModalData } from "@shared/data/modalData";
import { NxPasswordTouchToggleDirective } from "@shared/directives/nx-password-touch-toggle.directive";
import { LoginModel } from "@shared/interfaces/login.model";
import {
  GET_LOGIN_ALERT,
  RESET_CONCURRENT_LOGIN_ERROR,
} from "@store/login/login.action";
import { LoginState } from "@store/login/login.state";
import {
  LOGIN,
  RESET_LOGIN_RESPONSE,
  SAVE_USER_VALIDATION,
  SET_LOGGED_IN_STATE,
} from "@store/user/user.action";
import { UserState } from "@store/user/user.state";
import { Observable } from "rxjs";
import { ModalService } from "src/app/shared/services/modal.service";
import { environment } from "src/environments/environment";

import { PendingLoginValidationService } from "../pending-login-validation.service";

@Component({
  selector: "app-login",
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    ReactiveFormsModule,
    RouterModule,
    NxButtonModule,
    NxErrorModule,
    NxFormfieldModule,
    NxGridModule,
    NxIconModule,
    NxInputModule,
    NxLinkModule,
    NxMessageModule,
    NxPopoverModule,
    NxPasswordTouchToggleDirective,
    TranslateModule,
  ],
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(ModalService);

  protected readonly passwordMinLength = environment.passwordMinLength ?? 15;
  protected floatLabel;
  protected loginForm: FormGroup;
  protected isShowItHelpdesk: boolean = false;
  protected isShowConcurrentLoginModal: boolean = false;
  protected concurrentLoginErrorMsg: string = null;
  protected concurrentLoginErrorInfo: string = null;
  @Select(UserState.loginError) public loginError$;
  @Select(UserState.getErrorDesc) public getErrorDesc$;
  @Select(UserState.isUserSessionActive) public isUserSessionActive$;
  @Select(LoginState.getAlertMsg) public alertMsg$;
  @Select(LoginState.concurrentLoginError)
  public concurrentLoginError$: Observable<LoginModel["concurrentLoginState"]>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly router: Router,
    private readonly eleRef: ElementRef,
    private readonly crossAppNavigationService: CrossAppNavigationService,
    private readonly credentialStorage: CredentialStorageService,
    private readonly pendingLoginValidationService: PendingLoginValidationService,
  ) {}

  ngOnInit() {
    this.pendingLoginValidationService.clear();
    this.loginForm = this.fb.group({
      userId: ["", Validators.required],
      password: [
        "",
        Validators.compose([
          Validators.required,
          Validators.minLength(this.passwordMinLength),
        ]),
      ],
    });

    this.loginForm.reset();
    this.resetState();
    this.checkingLoginAlert();
  }

  //For Future Reference
  // ngAfterViewInit() {
  //   setTimeout(() => {
  //     this.initResize();
  //     this.changeDetector.detectChanges();
  //   }, 0);
  // }

  @HostListener("window:resize", ["$event"])
  protected onResize(_event) {
    const bannerEle =
      this.eleRef.nativeElement.querySelector(".login__banners");
    if (bannerEle) {
      bannerEle.style.height = window.innerHeight + "px";
    }
  }

  @HostListener("window:beforeunload") protected goToPage() {
    this.store.dispatch(new RESET_CONCURRENT_LOGIN_ERROR());
  }

  private initResize() {
    if (
      navigator.userAgent.indexOf("MSIE") !== -1 ||
      navigator.appVersion.indexOf("Trident/") > 0
    ) {
      //for IE or old browsers
      const evt = document.createEvent("UIEvents");
      evt.initEvent("resize", true, false);
      window.dispatchEvent(evt);
    } else {
      //for modern browsers
      window.dispatchEvent(new Event("resize"));
    }
  }

  private resetState() {
    const secretAnswerWrongPassword = this.store.selectSnapshot<any>(
      (state) => state.UserState.secretAnswerWrongPassword,
    );
    if (!secretAnswerWrongPassword) {
      this.store.dispatch(new RESET_LOGIN_RESPONSE());
    }
  }

  protected toggleTtHelpdesk() {
    const modalScreenClose = { disableClose: false };
    this.modalService.openDialog(
      ItHelpdeskModalComponent,
      "",
      modalScreenClose,
    );
  }
  private toggleConcurrentModal() {
    this.isShowConcurrentLoginModal = !this.isShowConcurrentLoginModal;
  }

  private checkingLoginAlert() {
    this.store.dispatch(new GET_LOGIN_ALERT());
  }
  protected onLogin() {
    if (!this.loginForm.valid) {
      return;
    }

    const userId = this.loginForm.get("userId").value;
    const password = this.loginForm.get("password").value;
    const loginReq = {
      userId: userId,
      password: password,
    };
    this.store.dispatch(new RESET_CONCURRENT_LOGIN_ERROR());

    this.store
      .dispatch(new LOGIN(loginReq))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const userState = this.store.selectSnapshot<any>(
          (state) => state.UserState,
        );

        if (userState.isUpdatePassword) {
          this.saveUserValidation();
          localStorage.setItem("loginSource", "sat");
          this.router.navigate(["/first-time-login"]);
        } else if (userState.isPasswordExpired) {
          this.saveUserValidation();
          localStorage.setItem("loginSource", "sat");
          this.router.navigate(["/password-expired"]);
        } else if (userState.isUserSessionActive) {
          this.modalService.openDialogWithCallbackHandler(
            CommonModalComponent,
            concurrentLoginModalData,
            (result) => {
              if (result) {
                localStorage.setItem("loginSource", "sat");
                this.navigateToSecretAnswer();
              }
            },
          );
        } else if (userState.user && !userState.error) {
          this.saveUserValidation();
          localStorage.setItem("loginSource", "sat");
          this.store
            .dispatch(new SET_LOGGED_IN_STATE())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async () => {
              if (environment.mockServerApiEnabled) {
                this.router.navigate(["/summary"]);
                return;
              }
              // Hand the freshly-authenticated credential to the browser's
              // password manager so it can offer to save it, then redirect.
              await this.credentialStorage.store(userId, password);
              this.crossAppNavigationService.redirectToAlphaSatPath(
                "/home?token=TOKEN",
              );
            });
        }
      });
  }

  private saveUserValidation() {
    const user = {
      tempUserId: this.loginForm.get("userId").value,
      tempPass: this.loginForm.get("password").value,
    };
    this.store.dispatch(new SAVE_USER_VALIDATION(user));
  }

  private savePendingUserValidation() {
    const user = {
      tempUserId: this.loginForm.get("userId").value,
      tempPass: this.loginForm.get("password").value,
    };
    this.pendingLoginValidationService.set(user);
    this.store.dispatch(new SAVE_USER_VALIDATION(user));
  }

  // Concurrent-login verification stays inside SAT so the user can continue
  // through this app's secret-answer route with the saved credentials.
  private navigateToSecretAnswer() {
    this.savePendingUserValidation();
    void this.router.navigate(["/secret-answer"]);
  }

  protected navigateToResetPassword() {
    this.crossAppNavigationService.redirectToAlphaSatPath(
      "/auth/reset-password",
    );
  }

  ngOnDestroy() {
    this.store.dispatch(new RESET_CONCURRENT_LOGIN_ERROR());
  }
}
